import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { AITextarea } from '@/components/ui/ai-textarea';
import type {
  MarketingStrategy,
  StrategyHistoryYear,
  ResearchFocusArea,
  StrategyStatus,
  PlanStatus,
  ActivityType,
} from '@/types';
import type { ApiResponse, PaginatedResponse } from '@/types';
import { api } from '@/lib/api';
import { ResearchWorkspace } from '@/components/research/ResearchWorkspace';
import { StrategyWizardPanel, type StrategyMainTab } from '@/components/strategy/StrategyWizardPanel';
import { StrategyHistoryComparisonChart } from '@/components/strategy/charts/StrategyHistoryComparisonChart';
import { StrategyChannelCharts } from '@/components/strategy/charts/StrategyChannelCharts';
import { StrategyMarketProfileRadar } from '@/components/strategy/charts/StrategyMarketProfileRadar';
import { StrategyKpiComparisonBars } from '@/components/strategy/charts/StrategyKpiComparisonBars';
import { hasAnyMarketProfileSignal } from '@/components/strategy/charts/strategy-chart-shared';
import { cn } from '@/lib/utils';
import { loadResearchUiDefaults } from '@/lib/research-ui-defaults';
import { useStrategyStore } from '@/stores/strategy';
import { useResearchQueueStore, RESEARCH_FINISHED_EVENT } from '@/stores/researchQueue';
import { toast } from 'sonner';
import {
  Target, Plus, Loader2, Search, Sparkles, RefreshCw,
  CheckCircle2, BarChart3, TrendingUp,
  MessageSquare, Globe, Users, Lightbulb, Save,
  ExternalLink, X, BookOpen, FileDown,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Checkbox } from '@/components/ui/checkbox';

/** Kalenderjahre im Dropdown (Mitte = aktuelles Jahr), damit ältere Strategien wählbar bleiben. */
const YEAR_OPTIONS = (() => {
  const y0 = new Date().getFullYear();
  return Array.from({ length: 14 }, (_, i) => y0 - 6 + i);
})();

const STATUS_LABELS: Record<StrategyStatus, string> = {
  draft: 'Entwurf',
  active: 'Aktiv',
  archived: 'Archiviert',
};

const STATUS_COLORS: Record<StrategyStatus, string> = {
  draft: 'bg-card-hover text-muted',
  active: 'bg-primary text-primary-foreground',
  archived: 'bg-card-hover text-muted',
};

/**
 * Liste/Hash liefern keine `research_runs` (nur `research_runs_count`). Wird dieselbe
 * Strategie-ID erneut gesetzt, würde ein naiver Replace die Detail-Daten zerstören;
 * `loadFullStrategy` läuft nur bei ID-Wechsel — daher Merge bei gleicher ID.
 */
function mergeStrategyListPick(
  prev: MarketingStrategy | null,
  next: MarketingStrategy | null
): MarketingStrategy | null {
  if (!next) return null;
  if (prev?.id === next.id) {
    return { ...prev, ...next };
  }
  return next;
}

export function MarketingStrategyPage() {
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  const [strategies, setStrategies] = useState<MarketingStrategy[]>([]);
  const [activeStrategy, setActiveStrategy] = useState<MarketingStrategy | null>(null);
  const [loading, setLoading] = useState(true);

  // Research tab state
  const [researchQuery, setResearchQuery] = useState('');
  const [focusArea, setFocusArea] = useState<ResearchFocusArea>('market');
  const [researchProvider, setResearchProvider] = useState<
    'openai' | 'modelrouter' | 'azure_foundry' | 'azure_cognitive_services' | 'custom_o3gpt'
  >('openai');
  const [startingResearch, setStartingResearch] = useState(false);
  const researchQueueItems = useResearchQueueStore((s) => s.items);
  const fetchResearchQueue = useResearchQueueStore((s) => s.fetchQueue);

  // Strategy generate state
  const [generating, setGenerating] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [saving, setSaving] = useState(false);

  // Inline edit form for strategy fields
  const [editForm, setEditForm] = useState<Partial<MarketingStrategy>>({});
  const [hasEdits, setHasEdits] = useState(false);

  // History state
  const [historyData, setHistoryData] = useState<StrategyHistoryYear[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Create strategy dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  /** Optional supplier links for cascade deactivation */
  const [newStrategySupplierIds, setNewStrategySupplierIds] = useState<string[]>([]);
  const [supplierOptions, setSupplierOptions] = useState<{ id: string; name: string }[]>([]);
  const [creating, setCreating] = useState(false);

  const [mainTab, setMainTab] = useState<StrategyMainTab>('research');
  const [wizardOpen, setWizardOpen] = useState(false);
  /** Soft-deleted strategies (z. B. nach Supplier-Deaktivierung oder „Löschen“) — ohne diesen Schalter unsichtbar. */
  const [includeInactiveStrategies, setIncludeInactiveStrategies] = useState(false);

  const { setStrategy: setGlobalStrategy, refresh: refreshGlobalStrategy } = useStrategyStore();

  const loadStrategies = useCallback(async (year: number) => {
    setLoading(true);
    try {
      const q = new URLSearchParams({
        year: String(year),
        limit: '100',
      });
      if (includeInactiveStrategies) q.set('include_inactive', '1');
      const res = await api.get<PaginatedResponse<MarketingStrategy>>(`/marketing-strategies?${q.toString()}`);
      const list = Array.isArray(res.data) ? res.data : [];
      if (typeof res.total === 'number' && res.total > list.length && list.length >= 100) {
        toast.info(
          `${res.total} Strategien für ${year} — Anzeige auf 100 begrenzt. Bitte Suche oder weiteren Filter in einer späteren Version.`,
          { duration: 7000 }
        );
      }
      setStrategies(list);
      // Auto-select active or most recent (bevorzugt weiterhin aktive Einträge)
      const active =
        list.find((s) => s.is_active !== false && s.status === 'active') ??
        list.find((s) => s.is_active !== false) ??
        list[0] ??
        null;
      setActiveStrategy((prev) => mergeStrategyListPick(prev, active));
      if (active) setEditForm(active);
    } catch (err) {
      setStrategies([]);
      setActiveStrategy(null);
      toast.error(err instanceof Error ? err.message : 'Strategien konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [includeInactiveStrategies]);

  useEffect(() => {
    void loadStrategies(selectedYear);
  }, [selectedYear, loadStrategies]);

  useEffect(() => {
    void api
      .get<ApiResponse<{ id: string; name: string }[]> & { data?: { id: string; name: string }[] }>('/suppliers?limit=100')
      .then((res) => {
        const raw = (res as { data?: { id: string; name: string }[] }).data;
        setSupplierOptions(Array.isArray(raw) ? raw : []);
      })
      .catch(() => setSupplierOptions([]));
  }, []);

  useEffect(() => {
    void loadResearchUiDefaults().then((d) => {
      if (!d) return;
      setFocusArea(d.focus_area);
      const p = d.provider === 'custom_o3gpt' ? 'openai' : d.provider;
      setResearchProvider(p);
    });
  }, []);

  const loadFullStrategy = useCallback(async (id: string) => {
    try {
      const res = await api.get<ApiResponse<MarketingStrategy>>(`/marketing-strategies/${id}`);
      if (res.data) {
        setActiveStrategy(res.data);
        setEditForm(res.data);
        setGlobalStrategy(res.data);
      }
    } catch {
      // ignore
    }
  }, [setGlobalStrategy]);

  useEffect(() => {
    if (!activeStrategy?.id) return;
    // List/Hash-Objekte haben kein `research_runs`; Detail-GET liefert immer ein Array.
    if (activeStrategy.research_runs != null) return;
    void loadFullStrategy(activeStrategy.id);
  }, [activeStrategy?.id, activeStrategy?.research_runs, loadFullStrategy]);

  useEffect(() => {
    if (activeStrategy?.id) {
      setHistoryLoading(true);
      api.get<ApiResponse<StrategyHistoryYear[]>>(`/marketing-strategies/${activeStrategy.id}/history-context`)
        .then((res) => setHistoryData(Array.isArray(res.data) ? res.data : []))
        .catch(() => setHistoryData([]))
        .finally(() => setHistoryLoading(false));
    }
  }, [activeStrategy?.id]);

  // Deep-link: /#/marketing-strategy?strategy=<uuid> (z. B. aus Topbar-Warteschlange)
  useEffect(() => {
    const apply = () => {
      const hash = window.location.hash.slice(1) || '';
      const qIdx = hash.indexOf('?');
      const q = qIdx >= 0 ? hash.slice(qIdx + 1) : '';
      const sid = new URLSearchParams(q).get('strategy');
      if (!sid || strategies.length === 0) return;
      const found = strategies.find((s) => s.id === sid);
      if (found) {
        setSelectedYear(found.year);
        setActiveStrategy((prev) => mergeStrategyListPick(prev, found));
        setEditForm(found);
      }
    };
    apply();
    window.addEventListener('hashchange', apply);
    return () => window.removeEventListener('hashchange', apply);
  }, [strategies]);

  useEffect(() => {
    const onFinished = (ev: Event) => {
      const e = ev as CustomEvent<{ strategyId: string }>;
      const sid = e.detail?.strategyId;
      if (!sid) return;
      if (sid === activeStrategy?.id) {
        void loadFullStrategy(sid);
        void refreshGlobalStrategy();
      }
    };
    window.addEventListener(RESEARCH_FINISHED_EVENT, onFinished);
    return () => window.removeEventListener(RESEARCH_FINISHED_EVENT, onFinished);
  }, [activeStrategy?.id, loadFullStrategy, refreshGlobalStrategy]);

  const hasRunningResearch = useMemo(
    () => activeStrategy?.research_runs?.some((r) => r.status === 'running') ?? false,
    [activeStrategy?.research_runs]
  );

  useEffect(() => {
    if (!activeStrategy?.id || !hasRunningResearch) return;
    const t = setInterval(() => {
      void loadFullStrategy(activeStrategy.id);
    }, 8000);
    return () => clearInterval(t);
  }, [activeStrategy?.id, hasRunningResearch, loadFullStrategy]);

  const handleStartResearch = async () => {
    if (!activeStrategy?.id || !researchQuery.trim()) {
      toast.error('Bitte zuerst eine Strategie auswählen und eine Frage eingeben.');
      return;
    }
    setStartingResearch(true);
    try {
      const res = await api.post<ApiResponse<{ run_id: string; status: string }>>(
        `/marketing-strategies/${activeStrategy.id}/research`,
        { query: researchQuery.trim(), focus_area: focusArea, provider: researchProvider }
      );
      if (res.data?.run_id) {
        setResearchQuery('');
        void fetchResearchQueue();
        toast.info('Research gestartet – Fortschritt in der Warteschlange (oben).');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Research konnte nicht gestartet werden.');
    } finally {
      setStartingResearch(false);
    }
  };

  const handleGenerate = async () => {
    if (!activeStrategy?.id) return;
    setGenerating(true);
    try {
      const res = await api.post<ApiResponse<MarketingStrategy>>(
        `/marketing-strategies/${activeStrategy.id}/generate`,
        undefined,
        { timeoutMs: 180_000 }
      );
      if (!res.success || !res.data) {
        toast.error(res.error || 'Generierung fehlgeschlagen.');
        return;
      }
      setActiveStrategy(res.data);
      setEditForm(res.data);
      setGlobalStrategy(res.data);
      setHasEdits(false);
      toast.success('Strategie erfolgreich generiert!');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Generierung fehlgeschlagen.');
    } finally {
      setGenerating(false);
    }
  };

  const handleExportStrategyPdf = async () => {
    if (!activeStrategy?.id) return;
    setPdfExporting(true);
    try {
      const blob = await api.getBlob(`/marketing-strategies/${activeStrategy.id}/pdf`);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `marketing-strategy-${activeStrategy.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success('PDF wurde heruntergeladen.');
    } catch {
      toast.error('PDF-Export ist fehlgeschlagen.');
    } finally {
      setPdfExporting(false);
    }
  };

  const handleSave = async () => {
    if (!activeStrategy?.id) return;
    setSaving(true);
    try {
      await api.patch(`/marketing-strategies/${activeStrategy.id}`, editForm);
      const updated = { ...activeStrategy, ...editForm } as MarketingStrategy;
      setActiveStrategy(updated);
      setGlobalStrategy(updated);
      setHasEdits(false);
      toast.success('Strategie gespeichert.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      const res = await api.post<ApiResponse<MarketingStrategy>>('/marketing-strategies', {
        year: selectedYear,
        title: newTitle.trim(),
        ...(newStrategySupplierIds.length > 0 ? { supplier_ids: newStrategySupplierIds } : {}),
      });
      if (res.data) {
        toast.success('Strategie erstellt!');
        setCreateDialogOpen(false);
        setNewTitle('');
        setNewStrategySupplierIds([]);
        await loadStrategies(selectedYear);
        setActiveStrategy(res.data);
        setEditForm(res.data);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Strategie konnte nicht erstellt werden.');
    } finally {
      setCreating(false);
    }
  };

  const updateEdit = (field: keyof MarketingStrategy, value: unknown) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
    setHasEdits(true);
  };

  return (
    <div className="flex h-full flex-col p-6">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-card-hover">
            <Target size={22} className="text-muted dark:text-foreground" />
          </div>
          <div className="max-w-3xl space-y-2">
            <h1 className="text-xl font-bold text-foreground">Marketingstrategie</h1>
            <p className="text-sm text-muted">Marktforschung & KI-Strategie</p>
          </div>
        </div>
        <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-3">
          {/* Year selector */}
          <Tooltip delayDuration={200}>
            <TooltipTrigger asChild>
              <div className="inline-flex items-center gap-1.5">
                <span className="text-xs text-muted">Jahr</span>
                <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v, 10))}>
                  <SelectTrigger className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {YEAR_OPTIONS.map((y) => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs text-sm">
              Nur Strategien dieses Kalenderjahres. Liegen Ihre Einträge z. B. in 2025/2026, wählen Sie dieses Jahr — sonst wirkt die Liste leer.
            </TooltipContent>
          </Tooltip>

          <div className="flex max-w-[11rem] items-start gap-2 rounded-md border border-border/80 bg-background/80 px-2.5 py-1.5 dark:border-border dark:bg-card/40">
            <Checkbox
              id="include-inactive-strategies"
              checked={includeInactiveStrategies}
              onCheckedChange={(v) => setIncludeInactiveStrategies(v === true)}
              className="mt-0.5"
            />
            <Label htmlFor="include-inactive-strategies" className="cursor-pointer text-xs font-normal leading-snug text-muted">
              Archivierte anzeigen
              <span className="mt-0.5 block text-[10px] text-muted">
                (z. B. nach Supplier-Stopp oder Entfernen)
              </span>
            </Label>
          </div>

          {/* Strategy selector */}
          {strategies.length > 0 && (
            <Select
              value={activeStrategy?.id ?? ''}
              onValueChange={(id) => {
                const s = strategies.find((st) => st.id === id);
                if (s) {
                  setActiveStrategy((prev) => mergeStrategyListPick(prev, s));
                  setEditForm(s);
                  setHasEdits(false);
                }
              }}
            >
              <SelectTrigger className="w-52 max-w-[min(100%,13rem)] truncate sm:max-w-none sm:w-52">
                <SelectValue placeholder="Strategie wählen" />
              </SelectTrigger>
              <SelectContent>
                {strategies.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    <span className="flex items-center gap-2">
                      <span
                        className={cn(
                          'h-2 w-2 shrink-0 rounded-full',
                          s.is_active === false ? 'bg-muted' : s.status === 'active' ? 'bg-primary' : s.status === 'draft' ? 'bg-muted' : 'bg-muted'
                        )}
                      />
                      <span className="truncate">
                        {s.title}
                        {s.is_active === false ? <span className="text-muted"> — archiviert</span> : null}
                      </span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <div className="ml-auto flex min-w-0 shrink-0 flex-wrap items-center justify-end gap-2">
            {strategies.length > 0 && activeStrategy && (
              <Button
                type="button"
                variant="outline"
                size="default"
                className="h-9 min-h-9 shrink-0 gap-2 border-border px-3 text-sm font-medium text-foreground hover:bg-background dark:hover:bg-card-hover"
                onClick={() => setWizardOpen(true)}
              >
                <Sparkles size={16} className="shrink-0" aria-hidden />
                <span className="whitespace-nowrap">Assistent</span>
              </Button>
            )}
            <Button
              type="button"
              size="default"
              className="h-9 min-h-9 shrink-0 gap-2 px-3 text-sm font-semibold shadow-sm"
              onClick={() => setCreateDialogOpen(true)}
            >
              <Plus size={16} className="shrink-0" aria-hidden />
              <span className="whitespace-nowrap">Neue Strategie</span>
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 size={32} className="animate-spin text-muted" />
        </div>
      ) : !activeStrategy ? (
        /* Empty state */
        <div className="flex flex-1 flex-col items-center justify-center gap-6">
          <div className="rounded-full bg-card-hover p-6">
            <Target size={40} className="text-muted" />
          </div>
          <div className="max-w-md text-center">
            <h2 className="text-lg font-semibold text-muted dark:text-foreground">
              Keine Strategie für {selectedYear}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-muted">
              Die Übersicht ist <strong className="font-medium text-muted">nach Kalenderjahr</strong>{' '}
              gefiltert. Wenn Sie viele Strategien angelegt haben, können diese in einem{' '}
              <strong className="font-medium text-muted">anderen Jahr</strong> liegen — bitte das Jahr
              oben rechts wechseln (z. B. {selectedYear - 1} oder {selectedYear - 2}).
            </p>
            <p className="mt-2 text-sm text-muted">
              Oder legen Sie eine neue Marketingstrategie für {selectedYear} an und starten Sie die Marktforschung.
            </p>
            {!includeInactiveStrategies && (
              <p className="mt-3 text-sm text-warning/90">
                Viele Einträge sind nur noch unter{' '}
                <strong className="font-medium">Archivierte anzeigen</strong> sichtbar (z. B. wenn ein Supplier
                deaktiviert wurde oder Strategien entfernt wurden — sie sind weiterhin in der Datenbank).
              </p>
            )}
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
              {YEAR_OPTIONS.includes(selectedYear - 1) && (
                <Button type="button" variant="secondary" size="sm" onClick={() => setSelectedYear(selectedYear - 1)}>
                  Jahr {selectedYear - 1}
                </Button>
              )}
              {YEAR_OPTIONS.includes(selectedYear - 2) && (
                <Button type="button" variant="secondary" size="sm" onClick={() => setSelectedYear(selectedYear - 2)}>
                  Jahr {selectedYear - 2}
                </Button>
              )}
              {YEAR_OPTIONS.includes(selectedYear + 1) && (
                <Button type="button" variant="outline" size="sm" onClick={() => setSelectedYear(selectedYear + 1)}>
                  Jahr {selectedYear + 1}
                </Button>
              )}
            </div>
          </div>
          <Button onClick={() => setCreateDialogOpen(true)} size="lg" className="gap-2">
            <Plus size={16} />
            Strategie erstellen
          </Button>
        </div>
      ) : (
        /* Main content */
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Strategy status bar */}
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <Badge className={cn('text-xs', STATUS_COLORS[activeStrategy.status])}>
              {STATUS_LABELS[activeStrategy.status]}
            </Badge>
            <span className="text-sm text-muted">
              {activeStrategy.title}
            </span>
            {activeStrategy.research_runs && (
              <span className="text-xs text-muted">
                · {activeStrategy.research_runs.length} Research-Run(s)
              </span>
            )}
            <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
              {hasEdits && (
                <>
                  <span className="text-xs text-warning">Ungespeicherte Änderungen</span>
                  <Button size="sm" onClick={handleSave} disabled={saving} className="gap-1.5">
                    {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                    Speichern
                  </Button>
                </>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 border-border text-muted hover:bg-background dark:text-foreground dark:hover:bg-card-hover/80"
                disabled={pdfExporting}
                onClick={() => void handleExportStrategyPdf()}
              >
                {pdfExporting ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <FileDown size={14} />
                )}
                PDF
              </Button>
            </div>
          </div>

          {activeStrategy.is_active === false && (
            <div className="mb-4 rounded-lg border border-warning/30 bg-warning-soft px-3 py-2 text-sm text-warning">
              Diese Strategie ist als <strong className="font-medium">inaktiv</strong> markiert (z. B. nach
              Supplier-Deaktivierung oder Entfernen). Unter „Archivierte anzeigen“ können Sie sie wieder finden.
            </div>
          )}

          <Tabs
            value={mainTab}
            onValueChange={(v) => setMainTab(v as StrategyMainTab)}
            className="flex flex-1 flex-col overflow-hidden"
          >
            <TabsList className="mb-4 w-fit">
              <TabsTrigger value="research" className="gap-2">
                <Search size={14} />
                Forschung
              </TabsTrigger>
              <TabsTrigger value="strategy" className="gap-2">
                <Sparkles size={14} />
                Strategie
              </TabsTrigger>
              <TabsTrigger value="history" className="gap-2">
                <BarChart3 size={14} />
                Vorjahre
              </TabsTrigger>
            </TabsList>

            {/* ── TAB 1: FORSCHUNG ── */}
            <TabsContent value="research" className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden pb-2 lg:min-h-[calc(100dvh-14rem)]">
                  {(() => {
                    const runs = activeStrategy.research_runs ?? [];
                    const queueHere = researchQueueItems.filter((i) => i.strategy_id === activeStrategy.id);
                    const queueNotice =
                      activeStrategy.id && queueHere.length > 0 ? (
                        <div className="flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-sm text-muted dark:bg-card">
                          <Loader2 size={14} className="animate-spin text-muted" />
                          Research läuft im Hintergrund — Fortschritt siehst du im Stapel-Symbol oben.
                        </div>
                      ) : null;

                    const emptyHint =
                      runs.length === 0 && queueHere.length > 0 ? (
                        <div className="space-y-3 text-center">
                          <Loader2 size={28} className="mx-auto animate-spin text-muted" />
                          <p className="text-sm font-medium text-foreground">
                            Research läuft — die Liste wird gleich ergänzt
                          </p>
                          <p className="text-xs text-muted">
                            Sobald der Lauf abgeschlossen ist, erscheint das Ergebnis in der Liste links.
                          </p>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => void loadFullStrategy(activeStrategy.id)}
                          >
                            <RefreshCw size={14} className="mr-1.5" /> Jetzt aktualisieren
                          </Button>
                        </div>
                      ) : runs.length === 0 ? (
                        <div>
                          <BookOpen size={24} className="mx-auto mb-2 text-muted" />
                          <p className="text-sm text-muted">Noch keine Research-Runs vorhanden.</p>
                          <p className="text-xs text-muted">Starte eine Anfrage im Bereich oben.</p>
                        </div>
                      ) : undefined;

                    return (
                      <ResearchWorkspace
                        runs={runs}
                        scope="strategy"
                        strategyId={activeStrategy.id}
                        strategyTitle={activeStrategy.title}
                        strategyYear={activeStrategy.year}
                        researchQuery={researchQuery}
                        setResearchQuery={setResearchQuery}
                        focusArea={focusArea}
                        setFocusArea={setFocusArea}
                        researchProvider={researchProvider}
                        setResearchProvider={setResearchProvider}
                        onStartResearch={() => void handleStartResearch()}
                        startingResearch={startingResearch}
                        onRefresh={() => {
                          void loadFullStrategy(activeStrategy.id);
                          void fetchResearchQueue();
                        }}
                        queueNotice={queueNotice}
                        emptyHint={emptyHint}
                      />
                    );
                  })()}
              </div>
            </TabsContent>

            {/* ── TAB 2: STRATEGIE ── */}
            <TabsContent value="strategy" className="flex-1 overflow-hidden">
              <ScrollArea className="h-full">
                <div className="space-y-6 pb-6">
                  {/* Generate button */}
                  <Card>
                    <CardContent className="flex items-center justify-between py-4">
                      <div>
                        <p className="font-medium text-foreground">KI-Strategie generieren</p>
                        <p className="text-sm text-muted">
                          Basierend auf {activeStrategy.research_runs?.length ?? 0} Research-Run(s) und Vorjahresdaten
                        </p>
                      </div>
                      <Button
                        onClick={() => void handleGenerate()}
                        disabled={generating}
                        variant="outline"
                        className="gap-2 border-border text-muted hover:bg-background"
                      >
                        {generating ? (
                          <><Loader2 size={14} className="animate-spin" /> Generiere…</>
                        ) : (
                          <><Sparkles size={14} /> Strategie generieren</>
                        )}
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Executive Summary */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Executive Summary</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <AITextarea
                        value={editForm.executive_summary ?? ''}
                        onChange={(e) => updateEdit('executive_summary', e.target.value)}
                        onValueChange={(v) => updateEdit('executive_summary', v)}
                        placeholder="Zusammenfassung der Marketingstrategie…"
                        rows={5}
                        aiFieldLabel="Executive Summary"
                        aiContext={`Marketingstrategie ${activeStrategy.year}: ${activeStrategy.title}`}
                      />
                    </CardContent>
                  </Card>

                  {(hasAnyMarketProfileSignal(activeStrategy) ||
                    (activeStrategy.channel_strategy?.channels?.length ?? 0) > 0) && (
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm">Visualisierungen</CardTitle>
                        <p className="text-xs text-muted">
                          Marktprofil und Kanäle aus den Strategiedaten — ergänzend zur Bearbeitung unten.
                        </p>
                      </CardHeader>
                      <CardContent className="space-y-8">
                        <StrategyMarketProfileRadar strategy={activeStrategy} />
                        <StrategyChannelCharts channels={activeStrategy.channel_strategy?.channels ?? []} />
                      </CardContent>
                    </Card>
                  )}

                  {/* Status */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Status</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Select
                        value={editForm.status ?? 'draft'}
                        onValueChange={(v) => updateEdit('status', v as StrategyStatus)}
                      >
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">Entwurf</SelectItem>
                          <SelectItem value="active">Aktiv</SelectItem>
                          <SelectItem value="archived">Archiviert</SelectItem>
                        </SelectContent>
                      </Select>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Co-Marketing Lieferanten (optional)</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <p className="text-xs text-muted">
                        Wenn mindestens ein Lieferant verknüpft ist, wird diese Strategie beim Deaktivieren eines dieser Lieferanten mit archiviert und aus Auswertungen genommen.
                      </p>
                      <div className="max-h-56 w-full max-w-full space-y-2 overflow-y-auto rounded-lg border border-border p-3 sm:max-w-2xl">
                        {supplierOptions.length === 0 ? (
                          <p className="text-xs text-muted">Keine Lieferanten geladen.</p>
                        ) : (
                          supplierOptions.map((s) => (
                              <label
                                key={s.id}
                                className="flex cursor-pointer items-start gap-3 rounded-md px-1 py-1.5 hover:bg-background dark:hover:bg-card-hover/80"
                              >
                                <Checkbox
                                  checked={(editForm.supplier_ids ?? []).includes(s.id)}
                                  onCheckedChange={() => {
                                    const cur = [...(editForm.supplier_ids ?? [])];
                                    const i = cur.indexOf(s.id);
                                    if (i >= 0) cur.splice(i, 1);
                                    else cur.push(s.id);
                                    updateEdit('supplier_ids', cur);
                                  }}
                                  className="mt-0.5"
                                  aria-label={s.name}
                                />
                                <span className="text-sm text-foreground">{s.name}</span>
                              </label>
                          ))
                        )}
                      </div>
                      {(editForm.supplier_ids?.length ?? 0) > 0 && (
                        <p className="text-xs text-muted">
                          {editForm.supplier_ids?.length ?? 0} Lieferant(en) ausgewählt
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Objectives */}
                  {activeStrategy.objectives && activeStrategy.objectives.length > 0 && (
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-sm">
                          <CheckCircle2 size={14} className="text-muted" />
                          Strategische Ziele
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {activeStrategy.objectives.map((obj) => (
                          <div key={obj.id} className="rounded-lg border border-border p-3">
                            <div className="flex items-start justify-between">
                              <p className="font-medium text-foreground">{obj.title}</p>
                              <Badge variant="outline" className="text-xs">{obj.timeframe}</Badge>
                            </div>
                            <p className="mt-1 text-xs text-muted">
                              KPI: <span className="font-medium">{obj.kpi}</span> · Ziel: {obj.target}
                            </p>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}

                  {/* KPIs */}
                  {activeStrategy.kpis && activeStrategy.kpis.length > 0 && (
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-sm">
                          <BarChart3 size={14} className="text-muted" />
                          KPIs
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        <div className="grid gap-3 sm:grid-cols-2">
                          {activeStrategy.kpis.map((kpi) => (
                            <div key={kpi.id} className="rounded-lg border border-border p-3">
                              <p className="text-sm font-medium text-muted">{kpi.name}</p>
                              <p className="text-xs text-muted">Ziel: {kpi.target} {kpi.unit}</p>
                              {kpi.current != null && (
                                <div className="mt-1.5">
                                  <Progress
                                    value={Math.min(100, Math.round((kpi.current / kpi.target) * 100))}
                                    className="h-1"
                                  />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        <StrategyKpiComparisonBars kpis={activeStrategy.kpis} />
                      </CardContent>
                    </Card>
                  )}

                  {/* Channel Strategy */}
                  {activeStrategy.channel_strategy?.channels && activeStrategy.channel_strategy.channels.length > 0 && (
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-sm">
                          <Globe size={14} className="text-muted" />
                          Kanalstrategie
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {[...activeStrategy.channel_strategy.channels]
                          .sort((a, b) => b.priority - a.priority)
                          .map((ch) => (
                            <div key={ch.name} className="rounded-lg border border-border p-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{ch.name}</span>
                                  <Badge variant="secondary" className="text-xs">Prio {ch.priority}/10</Badge>
                                </div>
                                <span className="text-sm font-medium text-muted">
                                  {ch.budget_pct}%
                                </span>
                              </div>
                              {ch.tactics?.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {ch.tactics.map((t) => (
                                    <span key={t} className="rounded bg-card-hover px-1.5 py-0.5 text-xs">
                                      {t}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                      </CardContent>
                    </Card>
                  )}

                  {/* Key Messages */}
                  {activeStrategy.key_messages && activeStrategy.key_messages.length > 0 && (
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-sm">
                          <MessageSquare size={14} className="text-muted" />
                          Kernbotschaften
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {activeStrategy.key_messages.map((msg) => (
                          <div key={msg.id} className="rounded-lg border border-border p-3">
                            <p className="text-sm italic text-muted">"{msg.message}"</p>
                            <div className="mt-1.5 flex gap-2">
                              <Badge variant="outline" className="text-xs">{msg.audience}</Badge>
                              <Badge variant="secondary" className="text-xs">{msg.channel}</Badge>
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}

                  {/* Lessons Learned */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2 text-sm">
                        <Lightbulb size={14} className="text-warning" />
                        Lessons Learned
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <AITextarea
                        value={editForm.lessons_learned ?? ''}
                        onChange={(e) => updateEdit('lessons_learned', e.target.value)}
                        onValueChange={(v) => updateEdit('lessons_learned', v)}
                        placeholder="Was lief gut? Was schlecht? Was für dieses Jahr anders machen?"
                        rows={4}
                        aiFieldLabel="Lessons Learned"
                        aiContext="Marketing Lessons Learned aus Vorjahren"
                      />
                    </CardContent>
                  </Card>

                  {hasEdits && (
                    <div className="flex justify-end">
                      <Button onClick={() => void handleSave()} disabled={saving} className="gap-2">
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                        Änderungen speichern
                      </Button>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            {/* ── TAB 3: VORJAHRE ── */}
            <TabsContent value="history" className="flex-1 overflow-hidden">
              <ScrollArea className="h-full">
                {historyLoading ? (
                  <div className="flex justify-center py-16">
                    <Loader2 size={28} className="animate-spin text-muted" />
                  </div>
                ) : historyData.length === 0 ? (
                  <div className="py-16 text-center">
                    <BarChart3 size={32} className="mx-auto mb-3 text-muted" />
                    <p className="text-muted">Keine Vorjahresdaten verfügbar.</p>
                  </div>
                ) : (
                  <div className="space-y-5 pb-6">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Vergleich Vorjahre (Pläne)</CardTitle>
                        <p className="text-xs text-muted">
                          Abschlussquote und Budget-Auslastung der verknüpften Marketingpläne vor {activeStrategy.year}.
                        </p>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <StrategyHistoryComparisonChart years={historyData} />
                      </CardContent>
                    </Card>
                    <div className="flex items-center gap-2 rounded-lg bg-background px-4 py-2.5 text-sm text-muted dark:bg-card">
                      <Lightbulb size={14} />
                      Diese Daten fliessen in die KI-Strategiegenerierung ein.
                    </div>
                    {historyData.map((year) => (
                      <HistoryYearCard key={year.year} data={year} />
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </div>
      )}

      {/* Create strategy dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Neue Marketingstrategie</DialogTitle>
            <DialogDescription>
              Strategie für {selectedYear} erstellen. Danach können Research-Runs gestartet und die KI-Generierung ausgeführt werden.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid gap-2">
              <Label>Titel</Label>
              <Input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder={`Marketingstrategie ${selectedYear}`}
                onKeyDown={(e) => { if (e.key === 'Enter') void handleCreate(); }}
                autoFocus
              />
            </div>
            <div className="grid gap-2">
              <Label>Jahr</Label>
              <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v, 10))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {YEAR_OPTIONS.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Lieferanten (optional)</Label>
              <div className="max-h-40 space-y-2 overflow-y-auto rounded-lg border border-border p-3">
                {supplierOptions.length === 0 ? (
                  <p className="text-xs text-muted">Keine Lieferanten geladen.</p>
                ) : (
                  supplierOptions.map((s) => (
                    <label
                      key={s.id}
                      className="flex cursor-pointer items-start gap-3 rounded-md px-1 py-1 hover:bg-background dark:hover:bg-card-hover/80"
                    >
                      <Checkbox
                        checked={newStrategySupplierIds.includes(s.id)}
                        onCheckedChange={() => {
                          setNewStrategySupplierIds((prev) =>
                            prev.includes(s.id) ? prev.filter((x) => x !== s.id) : [...prev, s.id]
                          );
                        }}
                        className="mt-0.5"
                        aria-label={s.name}
                      />
                      <span className="text-sm text-foreground">{s.name}</span>
                    </label>
                  ))
                )}
              </div>
              <p className="text-xs text-muted">
                Verknüpfung für spätere Deaktivierung: Die Strategie wird archiviert, wenn ein ausgewählter Lieferant deaktiviert wird.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              <X size={14} /> Abbrechen
            </Button>
            <Button onClick={() => void handleCreate()} disabled={creating || !newTitle.trim()}>
              {creating ? <><Loader2 size={14} className="animate-spin" /> Erstellen…</> : <><Plus size={14} /> Erstellen</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {activeStrategy && (
        <StrategyWizardPanel
          strategy={activeStrategy}
          open={wizardOpen}
          onOpenChange={setWizardOpen}
          mainTab={mainTab}
          onMainTabChange={setMainTab}
          onRefreshStrategy={() => void loadFullStrategy(activeStrategy.id)}
        />
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function HistoryYearCard({ data }: { data: StrategyHistoryYear }) {
  const goalsMet = Array.isArray(data.goals)
    ? (data.goals as Array<{ current?: string; target?: string }>).filter((g) => g.current && g.target && parseFloat(g.current) >= parseFloat(g.target)).length
    : 0;
  const totalGoals = Array.isArray(data.goals) ? data.goals.length : 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{data.year} – {data.title}</CardTitle>
          <div className="flex gap-2">
            <Badge
              className={cn(
                'text-xs',
                data.completion_rate >= 75 ? 'bg-card-hover text-muted' :
                data.completion_rate >= 50 ? 'bg-warning-soft text-warning' :
                'bg-danger-soft text-danger'
              )}
            >
              {data.completion_rate}% Aktivitäten
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Stats grid */}
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-lg bg-background p-3 dark:bg-card-hover/50">
            <p className="text-xs text-muted">Aktivitäten</p>
            <p className="text-lg font-semibold text-foreground">
              {data.activities_done}/{data.activities_total}
            </p>
          </div>
          <div className="rounded-lg bg-background p-3 dark:bg-card-hover/50">
            <p className="text-xs text-muted">Budget-Auslastung</p>
            <p className={cn(
              'text-lg font-semibold',
              data.budget_utilization > 90 ? 'text-danger' :
              data.budget_utilization > 70 ? 'text-muted' :
              'text-foreground'
            )}>
              {data.budget_utilization}%
            </p>
          </div>
          <div className="rounded-lg bg-background p-3 dark:bg-card-hover/50">
            <p className="text-xs text-muted">Ziele erreicht</p>
            <p className="text-lg font-semibold text-foreground">
              {totalGoals > 0 ? `${goalsMet}/${totalGoals}` : '–'}
            </p>
          </div>
        </div>

        {/* Progress bars */}
        <div className="space-y-2">
          <div>
            <div className="flex justify-between text-xs text-muted mb-1">
              <span>Abschlussrate</span>
              <span>{data.completion_rate}%</span>
            </div>
            <Progress value={data.completion_rate} className="h-1.5" />
          </div>
          {data.budget_utilization > 0 && (
            <div>
              <div className="flex justify-between text-xs text-muted mb-1">
                <span>Budget-Auslastung</span>
                <span>{data.budget_utilization}%</span>
              </div>
              <Progress
                value={Math.min(100, data.budget_utilization)}
                className={cn('h-1.5', data.budget_utilization > 100 ? '[&>div]:bg-danger' : '')}
              />
            </div>
          )}
        </div>

        {/* Top activities */}
        {data.top_activities.length > 0 && (
          <div>
            <p className="mb-2 text-xs font-medium text-muted">Top-Aktivitäten</p>
            <div className="space-y-1">
              {data.top_activities.map((act) => (
                <div key={act.id} className="flex items-center gap-2 text-xs">
                  <TrendingUp size={11} className="text-muted shrink-0" />
                  <span className="truncate text-muted">{act.title}</span>
                  <Badge variant="outline" className="shrink-0 px-1 py-0 text-[10px]">{act.type}</Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
